import UpIcon from "@mui/icons-material/ExpandLessRounded";
import DownIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";

import storage from "services/storage";
import useDocumentAccess from "features/company-documents/useDocumentAccess";

import {
  ExternalModulesConst,
  ExternalProModulesConst,
  InternalAdminModulesConst,
  InternalUserModulesConst,
} from "../../consts/ModulesConst";

import "./LayoutSidebarComponent.css";
import { useLayout } from "components/layout/LayoutComponentContext";
import { OverflowText } from "components";

const LayoutSidebarComponent = ({ isSidebarExpand, toggleDrawer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyProfile } = useLayout();

  const [currentPath, updatePath] = useState(window.location.href);
  const [isCollapsed, setCollapsed] = useState(true);

  const isAdmin = storage.getItem("type") === "ADMIN";
  const isInternalAdmin = ["ADMIN", "SUPER ADMIN"].includes(storage.getItem("role"));

  const documentAccess = useDocumentAccess(isAdmin && isInternalAdmin, location.pathname);

  const isPro = useMemo(
    () => companyProfile?.subscription_type === "PRO",
    [companyProfile],
  );

  const modules = isAdmin
    ? isInternalAdmin
      ? InternalAdminModulesConst
      : InternalUserModulesConst
    : isPro
    ? ExternalProModulesConst
    : ExternalModulesConst;

  useEffect(() => {
    updatePath(location.pathname);
  }, [location]);

  const isActiveMenu = (modulePath) => {
    return currentPath.startsWith(modulePath);
  };

  const selectMenu = (modulePath) => {
    if (currentPath === modulePath) return;

    navigate(modulePath);
    isSidebarExpand && toggleDrawer();
    setCollapsed(true);
  };

  const toggleCollapse = (event) => {
    setCollapsed(!isCollapsed);
  };

  const generateList = (module) => (
    <div key={module.key}>
      <ListItemButton
        className='sidebar-menu-item margin-0'
        onClick={toggleCollapse}
      >
        <ListItemIcon className='sidebar-menu-icon'>
          <img alt='module-icon' src={module.icon} />
        </ListItemIcon>

        {isSidebarExpand && (
          <>
            <ListItemText
              className='sidebar-menu-label'
              primary={module.label}
            />

            {isCollapsed ? <DownIcon /> : <UpIcon />}
          </>
        )}
      </ListItemButton>

      {isSidebarExpand && (
        <Collapse in={!isCollapsed} unmountOnExit>
          <List className='sidebar-menu-list-collapse' component='div'>
            {module.childrens.map((module) =>
              generateItem(module, isSidebarExpand),
            )}
          </List>
        </Collapse>
      )}
    </div>
  );

  const generateItem = (module, showLabel) => (
    <ListItemButton
      className='sidebar-menu-item'
      key={module.key}
      selected={isActiveMenu(module.path)}
      onClick={() => selectMenu(module.path)}
    >
      {module.icon && (
        <Tooltip arrow placement='right' title={showLabel ? "" : module.label}>
          <ListItemIcon className='sidebar-menu-icon'>
            {typeof module.icon === "string" ? (
              <img alt='module-icon' src={module.icon} />
            ) : (
              module.icon
            )}
          </ListItemIcon>
        </Tooltip>
      )}

      {showLabel && (
        <ListItemText className='sidebar-menu-label' primary={module.label} />
      )}
    </ListItemButton>
  );

  return (
    <aside
      className={`layout-sidebar${
        isSidebarExpand
          ? ` sidebar-expand ${isPro ? "w-[310px]" : "w-[260px]"}`
          : ""
      }`}
    >
      <div className='sidebar-toggle'>
        <IconButton onClick={toggleDrawer}>
          <ArrowForwardIosIcon className='sidebar-toggle-icon' />
        </IconButton>
        <span className='sidebar-toggle-label'>Hide Menu</span>
      </div>
      {!isAdmin && (
        <div
          className={`flex items-center py-6 gap-4 border-b border-[#CDD4D7] 
          ${isSidebarExpand ? "justify-start" : "justify-center"}`}
        >
          {companyProfile?.logo_url ? (
            <img
              alt='sidebar-logo'
              className='h-10 w-10 rounded-full shrink-0 object-cover'
              src={companyProfile?.logo_url}
            />
          ) : (
            <AccountCircleRoundedIcon
              viewBox='2 2 20 20'
              aria-label='account-profile-image-default'
              className='p-0'
              sx={{ height: "40px", width: "40px", color: "#ccc" }}
            />
          )}
          {isSidebarExpand && (
            <OverflowText
              text={companyProfile?.name}
              textClassName='font-bold text-sm'
              maxChar={35}
            />
          )}
        </div>
      )}

      <List className='sidebar-menu-list' component='nav'>
        {modules.filter(module => (module.key !== "document-review" || documentAccess.view) && (module.key !== "reminder-sender" || documentAccess.review)).map((module) =>
          module.path
            ? generateItem(module, isSidebarExpand)
            : generateList(module),
        )}
      </List>
    </aside>
  );
};

export default LayoutSidebarComponent;
